/**
 * NMS Store Launch · Preloader (animation from "Neemans Store 50 v28")
 * Fills the title/subtitle tokens ({number}, {area}, {city}) from the launch
 * settings and store, then lifts the curtain once the animation has finished
 * (about 1.6s, plus a short hold = v28's 1.8s minimum) and the page has loaded
 * (that wait is capped by data-max-wait), or straight away on Skip. Unlocks
 * scrolling and tells the other sections via nms-sl:intro:reveal / :done.
 *
 * Runs on any template. Without Launch · Settings (no nms-sl-core.js on the
 * page) it boots on its own with a minimal service and the section's tokens.
 */
(() => {
  const DRAW_MS = 1610; // last animation (subtitle) ends at 1190 + 420ms
  const HOLD_MS = 190; // v28 lifts no earlier than 1.8s

  class NMSSlIntro extends window.NMSBaseClass {
    constructor(element, service) {
      super();
      this.element = element;
      this.sl = service;
      this.sectionId = element.dataset.sectionId;
      this.timers = [];
      this.controller = new AbortController();
    }

    init() {
      super.init();
      this.#fillText();
      this.refs.skip?.addEventListener('click', () => this.reveal(), { signal: this.controller.signal });
      if (window.Shopify?.designMode) this.#bindEditor();
      if (!this.sl.introActive) return;

      // The curtain lifts only after the 50 has fully drawn (the real CSS animations, which start
      // whenever the overlay first paints) plus a short hold, and once the page has loaded.
      // "Longest wait" caps the wait for the page load only; it never cuts the animation short.
      const maxWait = +this.element.dataset.maxWait || 5000;
      const wait = ms => new Promise(resolve => this.timers.push(setTimeout(resolve, ms)));
      const loaded = new Promise(resolve => {
        if (document.readyState === 'complete') resolve();
        else window.addEventListener('load', resolve, { once: true, signal: this.controller.signal });
      });
      const drawn = this.#drawn().then(() => {
        this.element.querySelector('.nms-sl-intro__mark')?.classList.add('is-drawn');
        return wait(this.sl.reducedMotion ? 400 : HOLD_MS);
      });
      Promise.all([drawn, Promise.race([loaded, wait(maxWait)])]).then(() => this.reveal());
    }

    /** Resolves when the draw, label and bar animations have finished (D3 / M3 is on screen). */
    #drawn() {
      const running = this.element.getAnimations?.({ subtree: true })
        .filter(a => /^nms-sl-intro-(sweep|five|drop|up|bar)$/.test(a.animationName || '')) || [];
      if (!running.length) return Promise.resolve(); // reduced motion: the final frame is already shown
      // Safety: if a browser never reports the end, give up a little after the animation's length.
      return Promise.race([
        Promise.all(running.map(a => a.finished.catch(() => {}))),
        new Promise(resolve => this.timers.push(setTimeout(resolve, DRAW_MS + 1500))),
      ]);
    }

    #fillText() {
      const { fill } = this.sl.helpers;
      const L = this.sl.launch;
      const vars = { number: L.number, area: L.area, city: this.sl.launchStore?.city || '', date: L.dateShort };
      // A missing {city} must not leave a dangling ", ".
      const text = template => fill(template, vars).replace(/\s*,\s*$/, '').replace(/^\s*,\s*/, '').trim();
      const { title, sub } = this.refs;
      if (title) title.textContent = text(title.dataset.template);
      if (sub) sub.textContent = text(sub.dataset.template);
    }

    reveal() {
      if (this.revealing) return;
      this.revealing = true;
      this.timers.forEach(clearTimeout);
      this.element.classList.add('is-leaving');
      this.sl.introRevealed = true;
      this.sl.emit('intro:reveal');
      this.timers.push(setTimeout(() => this.#finish(), this.sl.reducedMotion ? 200 : 1000));
    }

    #finish() {
      document.documentElement.classList.remove('nms-sl-intro-active');
      this.element.classList.remove('is-preview', 'is-leaving');
      try {
        sessionStorage.setItem('nm_pre_s1', '1');
        this.#resetSavedOverflow();
      } catch (error) {
        /* private mode */
      }
      this.sl.emit('intro:done');
    }

    /**
     * GoKwik's side cart saves the page's overflow-y when it boots and puts it back, with
     * !important, when the cart closes. If it booted while the intro held the page
     * (overflow: hidden), closing the cart would leave the page unable to scroll.
     */
    #resetSavedOverflow() {
      [['html-el-overflow', document.documentElement], ['body-el-overflow', document.body]].forEach(([key, el]) => {
        if (sessionStorage.getItem(key) === null) return;
        sessionStorage.setItem(key, JSON.stringify(getComputedStyle(el).overflowY));
      });
    }

    #bindEditor() {
      const { signal } = this.controller;
      document.addEventListener('shopify:section:select', event => {
        if (event.detail.sectionId !== this.sectionId) return;
        // Showing the overlay (display: none → flex) restarts the D1 → D3 animation.
        this.revealing = false;
        this.element.classList.remove('is-leaving', 'is-preview');
        this.element.querySelector('.nms-sl-intro__mark')?.classList.remove('is-drawn');
        void this.element.offsetWidth;
        this.element.classList.add('is-preview');
      }, { signal });
      document.addEventListener('shopify:section:deselect', event => {
        if (event.detail.sectionId === this.sectionId) this.element.classList.remove('is-preview');
      }, { signal });
    }

    destroy() {
      this.timers.forEach(clearTimeout);
      this.controller.abort();
      document.documentElement.classList.remove('nms-sl-intro-active');
      super.destroy();
    }
  }

  const SELECTOR = '[data-nms-sl-intro]';
  const register = () => window.neemans.storeLaunch.register(SELECTOR, NMSSlIntro);

  /** Just what the intro uses from NMSStoreLaunch, for pages without Launch · Settings. */
  const standaloneService = element => {
    const { number = '', area = '', city = '' } = element.dataset;
    return {
      get introActive() {
        return document.documentElement.classList.contains('nms-sl-intro-active');
      },
      introRevealed: false,
      reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
      helpers: { fill: (template, vars) => String(template ?? '').replace(/\{(\w+)\}/g, (match, key) => (vars[key] ?? match)) },
      launch: { number, area, dateShort: '' },
      launchStore: { city },
      emit(type, detail = {}) {
        document.dispatchEvent(new CustomEvent(`nms-sl:${type}`, { detail }));
      },
    };
  };

  const instances = new Map();
  const bootStandalone = root => {
    root.querySelectorAll(SELECTOR).forEach(element => {
      if (instances.has(element)) return;
      const instance = new NMSSlIntro(element, standaloneService(element));
      instances.set(element, instance);
      instance.init();
    });
  };

  const start = () => {
    if (window.neemans?.storeLaunch) return register();
    if (document.querySelector('script[src*="nms-sl-core"]')) {
      document.addEventListener('nms-sl:ready', register, { once: true });
      return;
    }
    bootStandalone(document);
    if (!window.Shopify?.designMode) return;
    document.addEventListener('shopify:section:load', event => bootStandalone(event.target));
    document.addEventListener('shopify:section:unload', event => {
      event.target.querySelectorAll(SELECTOR).forEach(element => {
        instances.get(element)?.destroy();
        instances.delete(element);
      });
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
